import { GoogleAuth } from "@tento-ui/auth/google";
import { Toast } from "@tento-ui/components/toast/toast";
import { Tracing } from "@tento-core/tracing";
import { Network } from "@tento-lona/network";
import { LonaApi } from "../api";
import { Config } from "../core/config";
import { LonaFeatureFlags } from "../core/flags";
import { MicrosoftAuth, OAuthPopup } from "./oauth-popup";

/**
 * App-layer registry of calendar providers ("google" + "outlook").
 * UI surfaces consume this list
 * instead of branching on provider source strings: the integrations
 * selector maps descriptors to cards, the linked-accounts list and
 * expired-auth card dispatch reauth through the descriptor, and the
 * onboarding steps ask "is any calendar provider linked" rather than
 * "is google linked".
 *
 * Lives in the app layer (not `@tento-lona`) because connect/reauth
 * are DOM popup flows over `LonaApi` — the SDK stays DOM-free.
 * Capability-driven behavior (editability, native move, conference)
 * does NOT come from this registry; it rides each calendar's
 * server-computed `CalendarDisplay.capabilities`.
 */
export namespace CalendarProviders {
  export type AccountAction = {
    label: string;
    run: () => Promise<void>;
  };

  export type Descriptor = {
    /** Persisted source string (`auth.source`, `:~{source}:…` keys). */
    source: string;
    /** UI label ("Google", "Outlook"). */
    label: string;
    /** Integration-card icon path (full-color brand mark). */
    icon: string;
    /** Monochrome glyph path (single-fill 24×24, tinted via CSS mask)
     * for quiet surfaces like calendar-list section headers. */
    iconMono: string;
    /** Card copy, varying on whether any account is already linked. */
    connectDescription: (anyLinked: boolean) => string;
    /** Link a (new) account of this provider. */
    connect: () => Promise<void>;
    /** Re-authenticate an expired auth of this provider. */
    reauth: () => Promise<void>;
    /** Provider-specific per-account context-menu entries (google's
     * "Add Calendar Permissions" scope upgrade). */
    accountActions: (auth: Network.AuthDisplay) => AccountAction[];
  };

  async function googleSignin(scopes: string[]): Promise<void> {
    const code = await GoogleAuth.login(Config.GOOGLE_CLIENT_ID, scopes);
    await LonaApi.post("/api/user/signin", {
      query: {
        code,
        promo: null,
      },
    });
    Tracing.info("net")("google-signin-button/signin/");
  }

  const GOOGLE: Descriptor = {
    source: "google",
    label: "Google",
    icon: "/@/static/img/integrations/google-calendar-64-icon.png",
    iconMono: "/@/static/img/integrations/mono/google.svg",
    connectDescription: (anyLinked) =>
      anyLinked
        ? "Link an additional Google account"
        : "Link a Google account to create, edit and view your events",
    connect: async () => {
      await googleSignin(Config.GOOGLE_SCOPES);
      Toast.info("Google account linked");
    },
    reauth: async () => {
      await googleSignin(Config.GOOGLE_SCOPES);
      Toast.info("Successfully re-authenticated with Google");
    },
    accountActions: (auth) => [
      {
        label: "Add Calendar Permissions",
        run: async () => {
          await googleSignin(Config.GOOGLE_SCOPES_WITH_CALENDAR);
          Toast.info("Calendar permissions updated successfully");
        },
      },
      {
        label: "Enable Gmail reading",
        run: async () => {
          const scopes = [...new Set([
            ...Config.GOOGLE_SCOPES,
            ...auth.scopes.split(/\s+/).filter(Boolean),
            "https://www.googleapis.com/auth/gmail.readonly",
          ])];
          const code = await GoogleAuth.login(Config.GOOGLE_CLIENT_ID, scopes, { hint: auth.id });
          await LonaApi.post("/api/mail/connect", { body: { authId: auth.id, code } });
          Toast.info("Gmail reading enabled");
        },
      },
    ],
  };

  /**
   * Whoop-style popup connect/reauth. Resolves when the flow settles —
   * success, server-reported failure, popup-blocked, or the user just
   * closing the popup — so `await connect()` callers always proceed.
   */
  function outlookPopup(successToast: string): Promise<void> {
    return new Promise((resolve) => {
      const opened = OAuthPopup.open({
        authUrl: MicrosoftAuth.authorizeUrl(MicrosoftAuth.CONNECT_REDIRECT_PATH),
        source: "outlook",
        onSuccess: () => {
          Toast.info(successToast);
          resolve();
        },
        onFailure: (reason) => {
          Toast.error(
            reason === "access_denied"
              ? "Microsoft sign-in was cancelled"
              : "Couldn't link the Microsoft account",
          );
          resolve();
        },
        onDismiss: () => resolve(),
      });
      if (!opened) {
        Toast.error("Popup blocked — allow popups for this site and try again");
        resolve();
      }
    });
  }

  const OUTLOOK: Descriptor = {
    source: "outlook",
    label: "Outlook",
    icon: "/@/static/img/integrations/microsoft-64-icon.svg",
    iconMono: "/@/static/img/integrations/mono/microsoft.svg",
    connectDescription: (anyLinked) =>
      anyLinked
        ? "Link an additional Outlook account"
        : "Link an Outlook account to create, edit and view your events",
    connect: () => outlookPopup("Outlook account linked"),
    reauth: () => outlookPopup("Successfully re-authenticated with Outlook"),
    // No scope-upgrade action: the connect flow already requests the
    // full Calendars.ReadWrite scope set. Accounts linked before that
    // bump still hold read-only tokens — they recover through `reauth`,
    // driven by the `calendar_read_only` write rejection.
    accountActions: () => [],
  };

  // Outlook hides itself while the Entra registration is unconfigured
  // (mirrors the signin button's gate) AND while the `outlook-provider`
  // debug flag is off — outlook surfaces are debug-gated until Slice 1
  // ships. Google stays first — surfaces like no-calendars.modal treat
  // list()[0] as the default provider. Computed per call so a /dev
  // flag toggle takes effect without a reload.
  function registry(): Descriptor[] {
    return MicrosoftAuth.isConfigured() &&
        LonaFeatureFlags.debug.outlookProvider.value
      ? [GOOGLE, OUTLOOK]
      : [GOOGLE];
  }

  export function list(): Descriptor[] {
    return registry();
  }

  export function get(source: string): Option<Descriptor> {
    return registry().find((d) => d.source === source) ?? null;
  }

  /**
   * The provider's name for `source` ("Google", "Outlook"), whether or not
   * its surfaces are enabled here: what a message about one of its calendars
   * says. An unknown source is named by its source string.
   */
  export function labelOf(source: string): string {
    return [GOOGLE, OUTLOOK].find((d) => d.source === source)?.label ?? source;
  }

  export function isCalendarSource(source: string): boolean {
    return get(source) !== null;
  }

  /** Whether any of `auth` belongs to a registered calendar provider. */
  export function anyLinked(auth: readonly Network.AuthDisplay[]): boolean {
    return auth.some((a) => isCalendarSource(a.source));
  }

  /** The first calendar-provider auth in `auth`, if any. */
  export function firstLinked(
    auth: readonly Network.AuthDisplay[],
  ): Option<Network.AuthDisplay> {
    return auth.find((a) => isCalendarSource(a.source)) ?? null;
  }
}
